import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SignedIn, SignedOut, UserButton, useUser, IS_DEMO_MODE } from '@/lib/auth';
import { useClient } from '@/hooks/useClient';
import { cn } from '@/lib/cn';
import {
  LayoutDashboard,
  ClipboardCheck,
  Film,
  Calendar,
  Bot,
  TrendingUp,
  Activity,
  Settings,
  AlertCircle,
  Loader2,
  Menu,
  X,
} from 'lucide-react';

export function ClientLayout() {
  const { isLoaded, isSignedIn } = useUser();
  const navigate = useNavigate();
  const { data, isLoading, error } = useClient();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/sign-in', { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Redirect vers le form de config si le coach n'a pas encore rempli ses
  // préférences (ICP, piliers, ton). Sans ça, les agents tournent à vide.
  useEffect(() => {
    if (data && data.client && !data.client.has_config) {
      navigate(`/clients/${data.client.slug}/setup`, { replace: true });
    }
  }, [data, navigate]);

  if (!isLoaded || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ducens-muted" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-ducens-danger mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Client introuvable</h1>
          <p className="text-ducens-muted text-sm">
            Le client demandé n'existe pas ou tu n'y as pas accès.
            {IS_DEMO_MODE && (
              <>
                <br />
                <br />
                <strong>Mode démo :</strong> seul le client <code>paja</code> est mocké. Va sur{' '}
                <code>/clients/paja</code>.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (!data.canAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-ducens-warn mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Accès refusé</h1>
          <p className="text-ducens-muted text-sm">
            Tu n'as pas les droits pour voir l'espace de {data.client.company_name}.
          </p>
        </div>
      </div>
    );
  }

  const slug = data.client.metadata.schema_isolation as string;

  return (
    <SignedIn>
      <div className="min-h-screen flex flex-col bg-ducens-bg">
        <Header clientName={data.client.company_name} onMenuClick={() => setMobileOpen(!mobileOpen)} />
        <div className="flex-1 flex relative">
          <Sidebar slug={slug} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
          <main className="flex-1 px-4 lg:px-6 py-6 lg:py-8 max-w-7xl mx-auto w-full">
            <Outlet context={data} />
          </main>
        </div>
      </div>
    </SignedIn>
  );
}

function Header({ clientName, onMenuClick }: { clientName: string; onMenuClick: () => void }) {
  return (
    <header className="border-b border-ducens-border bg-white sticky top-0 z-30">
      <div className="px-4 lg:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onMenuClick}
            className="lg:hidden p-1.5 -ml-1.5 hover:bg-ducens-bg rounded-md"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="w-8 h-8 rounded-lg bg-ducens-ink flex items-center justify-center text-ducens-accent font-bold">
            F
          </div>
          <div>
            <div className="font-semibold text-sm leading-tight">Ducens</div>
            <div className="text-xs text-ducens-muted leading-tight">{clientName}</div>
          </div>
          {IS_DEMO_MODE && (
            <span className="ml-3 hidden sm:inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-ducens-accent/40 border border-ducens-accent text-ducens-ink uppercase tracking-wide">
              démo
            </span>
          )}
        </div>
        <SignedIn>
          <UserButton />
        </SignedIn>
        <SignedOut>
          <NavLink to="/sign-in" className="text-sm text-ducens-muted hover:text-ducens-ink">
            Se connecter
          </NavLink>
        </SignedOut>
      </div>
    </header>
  );
}

function Sidebar({
  slug,
  mobileOpen,
  onClose,
}: {
  slug: string;
  mobileOpen: boolean;
  onClose: () => void;
}) {
  const items = [
    { to: `/clients/${slug}`, label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
    { to: `/clients/${slug}/approvals`, label: 'Validations', icon: ClipboardCheck, end: false },
    { to: `/clients/${slug}/content`, label: 'Contenus', icon: Film, end: false },
    { to: `/clients/${slug}/calendar`, label: 'Calendrier', icon: Calendar, end: false },
    { to: `/clients/${slug}/agents`, label: 'Agents', icon: Bot, end: false },
    { to: `/clients/${slug}/finance`, label: 'Finance', icon: TrendingUp, end: false },
    { to: `/clients/${slug}/system`, label: 'Système', icon: Activity, end: false },
    { to: `/clients/${slug}/settings`, label: 'Réglages', icon: Settings, end: false },
  ];

  const adminLink = `/admin/clients`;

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-20 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <nav
        className={cn(
          'fixed lg:sticky top-0 lg:top-12 left-0 z-30 w-64 lg:w-56 h-screen lg:h-[calc(100vh-49px)] shrink-0',
          'border-r border-ducens-border bg-white p-3 transition-transform',
          'lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex justify-between items-center lg:hidden mb-3 px-1">
          <span className="text-sm font-semibold">Menu</span>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-ducens-bg rounded-md"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul className="space-y-0.5">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive ? 'bg-ducens-ink text-white' : 'text-ducens-ink hover:bg-ducens-bg'
                  )
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="mt-6 pt-3 border-t border-ducens-border">
          <NavLink
            to={adminLink}
            onClick={onClose}
            className="flex items-center gap-2.5 px-3 py-2 rounded-md text-xs uppercase tracking-wide text-ducens-muted hover:bg-ducens-bg hover:text-ducens-ink"
          >
            ← Tous les clients (admin)
          </NavLink>
        </div>
      </nav>
    </>
  );
}
